import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../contexts/AuthContext';
import { useApprovedModels } from '../hooks/useApprovedModels';
import ApprovedModelsTable from '../components/approvedModels/ApprovedModelsTable';
import ApprovedModelForm from '../components/approvedModels/ApprovedModelForm';

const ApprovedModelsPage = () => {
  const { hasPermission } = useAuth();
  const {
    models,
    loading,
    error,
    successMessage,
    createModel,
    updateModel,
    deactivateModel,
    deprecateModel,
    deleteModel,
    checkUsage,
    detectCapabilities,
    clearError,
    clearSuccessMessage
  } = useApprovedModels();

  // Modal states
  const [formOpen, setFormOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, model: null });
  const [usageDialog, setUsageDialog] = useState({ open: false, data: null });

  // Check admin permission
  const isAdmin = hasPermission('approved_models', 'manage');

  // Handlers
  const handleAdd = () => {
    setSelectedModel(null);
    setFormOpen(true);
  };

  const handleEdit = (model) => {
    setSelectedModel(model);
    setFormOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedModel) {
        await updateModel(selectedModel.id, data);
      } else {
        await createModel(data);
      }
      setFormOpen(false);
      setSelectedModel(null);
    } catch (err) {
      // Error handled by hook
    }
  };

  const openConfirmDialog = (type, model, title, message) => {
    setConfirmDialog({ open: true, type, model, title, message });
  };

  const handleDeactivate = (model) => {
    openConfirmDialog('deactivate', model, 'Deactivate Model',
      `Are you sure you want to deactivate "${model.name}"? This will prevent it from being used in new customer configurations.`);
  };

  const handleDeprecate = (model) => {
    openConfirmDialog('deprecate', model, 'Deprecate Model',
      `Are you sure you want to deprecate "${model.name}"? Existing customers can continue using it, but it won't be available for new configurations.`);
  };

  const handleDelete = (model) => {
    openConfirmDialog('delete', model, 'Delete Model',
      `Are you sure you want to delete "${model.name}"? This action cannot be undone.`);
  };

  const handleViewUsage = async (model) => {
    try {
      const usageData = await checkUsage(model.id);
      setUsageDialog({ open: true, data: usageData, model });
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDetectCapabilities = async (model) => {
    try {
      await detectCapabilities(model.id);
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleConfirmAction = async () => {
    const { type, model } = confirmDialog;
    try {
      if (type === 'deactivate') {
        await deactivateModel(model.id);
      } else if (type === 'deprecate') {
        await deprecateModel(model.id);
      } else if (type === 'delete') {
        await deleteModel(model.id);
      }
      setConfirmDialog({ open: false, type: null, model: null });
    } catch (err) {
      // Error handled by hook, but keep dialog open to show error
      setConfirmDialog({ open: false, type: null, model: null });
    }
  };

  // Unauthorized access
  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          You do not have permission to access this page. Admin access is required.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Approved Bedrock Models
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add New Model
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Models Table */}
      {!loading && (
        <ApprovedModelsTable
          models={models}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          onDeprecate={handleDeprecate}
          onDelete={handleDelete}
          onViewUsage={handleViewUsage}
          onDetectCapabilities={handleDetectCapabilities}
        />
      )}

      {/* Form Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedModel ? 'Edit Approved Model' : 'Add New Approved Model'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <ApprovedModelForm model={selectedModel} onSave={handleSave} onCancel={() => setFormOpen(false)} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: null, model: null })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent><Typography>{confirmDialog.message}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null, model: null })}>Cancel</Button>
          <Button onClick={handleConfirmAction} color={confirmDialog.type === 'delete' ? 'error' : 'warning'} variant="contained">
            {confirmDialog.type === 'delete' ? 'Delete' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={usageDialog.open} onClose={() => setUsageDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Model Usage: {usageDialog.model?.name}</DialogTitle>
        <DialogContent>
          {usageDialog.data?.isInUse ? (
            <>
              <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Global Configuration:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="body2">
                  Used in: {usageDialog.data.globalConfigurationPositions?.join(', ')}
                </Typography>
              </Paper>
            </>
          ) : (
            <Typography variant="body1">This model is not currently in use by the global configuration.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageDialog({ open: false, data: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={clearSuccessMessage} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={clearSuccessMessage} severity="success" sx={{ width: '100%' }}>{successMessage}</Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={clearError} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={clearError} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </Container>
  );
};

export default ApprovedModelsPage;
